/**
 * SETTINGS PAGE — Hub delle impostazioni dell'applicazione, accessibile dalla
 * voce "Impostazioni" del menu laterale. Layout a 2 colonne (stesso pattern di
 * UserSettingsPage): a sinistra l'elenco delle sotto-sezioni, a destra la
 * sezione aperta.
 *
 * La sezione attiva e' riflessa nell'URL: /settings (azienda, default),
 * /settings/teams (utenti e team), /settings/pipelines, /settings/queues,
 * /settings/gestione-picklist (FILO-16), /settings/zone-e-assegnazioni (FILO-29), /settings/fiere
 * (FILO-67, le regole dei task di follow-up dei contatti rapidi; sparisce con la voce Fiere spenta).
 * Lo stato deriva dal path param, quindi
 * deep-link e back/forward del browser funzionano. I FORM di creazione/modifica
 * pipeline sono rotte a sé (/settings/pipelines/new, /settings/pipelines/:id/edit)
 * in coreRoutes.
 *
 * "Configurazione applicazione" NON c'e' piu'. Era un segnaposto: un titolo e
 * la riga "Da implementare". Una voce di menu che si apre sul nulla fa dubitare
 * anche delle altre, e in un pannello di impostazioni — dove si entra per
 * cambiare qualcosa — costa piu' di quanto renda. Torna quando ha un contenuto.
 * Nota: /settings/config non e' un 404, ricade sulla sezione azienda insieme a
 * qualsiasi altro segmento sconosciuto (vedi sectionFromParam).
 *
 * Dopo le sezioni del core vengono quelle dei PLUGIN, dallo slot
 * 'settings:section' (entityType 'Settings'): voce dal `labelKey`, URL
 * /settings/<slug>. Il registry rifiuta al boot uno slug che coincide con una
 * sezione del core, quindi qui il core vince sempre senza bisogno di controlli.
 *
 * "Utenti e team" stava nelle impostazioni UTENTE (il menu in alto a destra):
 * è configurazione dell'installazione, non preferenze di chi è loggato, quindi
 * vive qui insieme a code e pipeline. Il vecchio /settings/user/teams resta
 * come redirect in coreRoutes.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CompanySettings } from '../../components/settings/CompanySettings';
import { UsersTeamSettings } from '../../components/settings/UsersTeamSettings';
import { PicklistSettings } from '../../components/settings/PicklistSettings';
import { ZoneSettings } from '../../components/settings/ZoneSettings';
import { FairSettings } from '../../components/settings/FairSettings';
import QueueList from '../../entities/queue/QueueList';
import PipelineList from '../../entities/pipeline/PipelineList';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { useDisabledCoreItems } from '../../plugin/useCoreItems';

type SettingsSection = 'pipelines' | 'queues' | 'company' | 'team' | 'picklists' | 'zones' | 'fairs';

// Mappa sezione -> URL. La sezione di DEFAULT tiene il path base senza segmento,
// le altre hanno il proprio sotto-path: e' "azienda", la prima del nav, quindi il
// base path è suo.
const PATH_BY_SECTION: Record<SettingsSection, string> = {
  company: '/settings',
  team: '/settings/teams',
  pipelines: '/settings/pipelines',
  queues: '/settings/queues',
  picklists: '/settings/gestione-picklist',
  zones: '/settings/zone-e-assegnazioni',
  fairs: '/settings/fiere',
};

/**
 * Risolve la sezione attiva dal path param; default azienda (path base).
 *
 * Il vecchio /settings/company continua ad aprire l'azienda: non ha più un ramo
 * suo, ma essendo azienda il default ci finisce comunque insieme a qualsiasi
 * altro segmento sconosciuto.
 */
function sectionFromParam(section: string | undefined): SettingsSection {
  if (section === 'teams') return 'team';
  if (section === 'pipelines') return 'pipelines';
  if (section === 'queues') return 'queues';
  if (section === 'gestione-picklist') return 'picklists';
  if (section === 'zone-e-assegnazioni') return 'zones';
  if (section === 'fiere') return 'fairs';
  return 'company';
}

export function SettingsPage() {
  const { t } = useTranslation();
  // Le voci di una picklist hanno una rotta propria, senza :section: la sezione è la loro.
  const { section: sectionParam, listTypeDefinitionId } = useParams();
  const section = listTypeDefinitionId ? 'gestione-picklist' : sectionParam;
  const navigate = useNavigate();
  const pluginSections = useSlotContributors('Settings', 'settings:section');
  const fairsEnabled = !useDisabledCoreItems().has('rapid-contacts');
  const activePlugin = pluginSections.find((c) => c.slug === section);
  // Una sezione di plugin attiva vuol dire nessuna sezione core attiva: senza
  // questo, il fallback di sectionFromParam accenderebbe anche «azienda».
  const active = activePlugin ? null : sectionFromParam(section);

  // L'ordine del nav e' quello di lettura, non quello dei path: prima CHI usa il
  // CRM (l'azienda e le sue persone), poi COME lavora (pipeline, code).
  // L'array e' l'unica fonte dell'ordine: per spostare una voce basta
  // spostarla qui.
  const sections: { key: SettingsSection; label: string }[] = [
    { key: 'company', label: t('settings.nav.company') },
    { key: 'team', label: t('settings.nav.team') },
    { key: 'pipelines', label: t('settings.nav.pipelines') },
    { key: 'queues', label: t('settings.nav.queues') },
    { key: 'picklists', label: t('settings.nav.picklists') },
    { key: 'zones', label: t('settings.nav.zones') },
    // Le regole dei task delle fiere hanno senso solo dove la voce Fiere è accesa (FILO-67).
    ...(fairsEnabled ? [{ key: 'fairs' as const, label: t('settings.nav.fairs') }] : []),
  ];

  return (
    <div className="settings-layout">
      <nav className="settings-nav">
        <h2 className="settings-nav__title">{t('settings.title')}</h2>
        <ul className="settings-nav__list">
          {sections.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                className={`settings-nav__item${active === s.key ? ' settings-nav__item--active' : ''}`}
                onClick={() => navigate(PATH_BY_SECTION[s.key])}
              >
                <span>{s.label}</span>
              </button>
            </li>
          ))}
          {pluginSections.map((c) => (
            <li key={c.key}>
              <button
                type="button"
                className={`settings-nav__item${activePlugin === c ? ' settings-nav__item--active' : ''}`}
                onClick={() => navigate(`/settings/${c.slug}`)}
              >
                <span>{c.labelKey ? t(c.labelKey) : c.key}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <section className="settings-content">
        {active === 'pipelines' && <PipelineList />}
        {active === 'queues' && <QueueList />}
        {active === 'company' && <CompanySettings />}
        {active === 'team' && <UsersTeamSettings />}
        {active === 'picklists' && <PicklistSettings />}
        {active === 'zones' && <ZoneSettings />}
        {active === 'fairs' && fairsEnabled && <FairSettings />}
        {activePlugin && <activePlugin.component entityId={0} />}
      </section>
    </div>
  );
}